import {inject} from '@angular/core';
import {HttpErrorResponse, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {tap} from 'rxjs';
import {PosthogService} from './posthog.service';

// ponytail: one report per (method, route, status) and per minute in a tab, so an engine down does not send one per request.
const REPORT_INTERVAL_MS = 60_000;
const lastReports = new Map<string, number>();

/**
 * Sends to PostHog the engine failures (5xx) and the requests that got no answer (offline, engine unreachable): the
 * services turn most of them into a fallback value, so without this nobody knows a screen showed wrong data (KAB-2441).
 * Only the method, the route (ids and e-mails masked), the status and the engine's error type: no body, no query string.
 * Registered after the authentication interceptor, it also sees the requests that one replays.
 */
export const httpErrorReportingInterceptor: HttpInterceptorFn = (req, next) => {
	const posthog = inject(PosthogService);
	return next(req).pipe(
		tap({
			error: (error: unknown) => {
				if (error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500)) {
					try {
						report(posthog, req, error);
					} catch {
						// Reporting never changes the failure the caller gets.
					}
				}
			},
		}),
	);
};

function report(posthog: PosthogService, req: HttpRequest<unknown>, error: HttpErrorResponse): void {
	const route = routeOf(req.url);
	const key = `${req.method} ${route} ${error.status}`;
	const now = Date.now();
	if (now - (lastReports.get(key) ?? 0) < REPORT_INTERVAL_MS) {
		return;
	}
	lastReports.set(key, now);
	// No answer, no engine body: `error.error` is then the ProgressEvent of the failed request.
	const type: unknown = error.status === 0 ? undefined : error.error?.type;
	posthog.captureException(new Error(`HTTP ${error.status} ${req.method} ${route}`), {
		http_method: req.method,
		http_route: route,
		http_status: error.status,
		error_type: typeof type === 'string' && /^[a-z_]+$/.test(type) ? type : 'none',
	});
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The path of [url], without host nor query string, its ids (UUIDs, numbers) and e-mails replaced: `/tenants/:id/servers/:id`. */
export function routeOf(url: string): string {
	return new URL(url, window.location.origin).pathname
		.split('/')
		.map(segment => UUID.test(segment) || /^\d+$/.test(segment) ? ':id'
			: segment.includes('@') || segment.toLowerCase().includes('%40') ? ':email'
			: segment)
		.join('/');
}
